import {
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { SendSmsStatus } from '@fmnts/api/next';
import {
  ActionResponse,
  ActionResponseError,
  ActionService,
  isActionResponse,
  isActionStartEvent,
} from '@fmnts/common/actions';
import { logger } from '@fmnts/common/log';
import { arrayMapToSome, booleanInvert } from '@fmnts/core/rxjs';
import {
  AbstractDeclarativeFieldComponent,
  FormActionField,
} from '@fmnts/declarative-forms';
import { I18nModule } from '@fmnts/i18n';
import { DeclarativeFormFieldKey } from '@formunauts/shared/declarative-forms/domain';
import { ToastMessageService } from '@formunauts/shared/messaging/ui';
import { faSms } from '@fortawesome/pro-regular-svg-icons';
import { faCommentSlash, faWifiSlash } from '@fortawesome/pro-solid-svg-icons';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  PhoneAction,
  ValidateMobileNumberArgs,
} from '../../features/phone/phone-action.service';
interface InitActionResult {
  /**
   * Observable that controls the disabled state
   */
  isDisabled$: Observable<boolean>;
  /**
   * The action to run
   */
  action$: Observable<any>;
  /**
   * Action response handler
   */
  responseHandler(this: void, response: ActionResponse<any>): void;
}

/**
 * @returns
 * Stub for all unknown action that disables the button
 */
function createEmptyAction(): InitActionResult {
  return {
    isDisabled$: of(true),
    action$: EMPTY,
    responseHandler: () => {},
  };
}

const toastMessages = {
  // Toast messages for action `mobile_number_validation`
  mobile_number_validation: {
    [SendSmsStatus.Valid]: {
      key: 'mobile_number_validation.valid',
      text: 'mobile_number_validation.valid.text',
      title: 'mobile_number_validation.valid.title',
      icon: faSms,
    },
    [SendSmsStatus.Invalid]: {
      key: 'mobile_number_validation.invalid',
      text: 'mobile_number_validation.invalid.text',
      title: 'mobile_number_validation.invalid.title',
      icon: faCommentSlash,
    },
    [SendSmsStatus.NetworkError]: {
      key: 'mobile_number_validation.network_error',
      text: 'mobile_number_validation.network_error.text',
      title: 'mobile_number_validation.network_error.title',
      icon: faWifiSlash,
    },
  },
};

/**
 * Component to display a generic button on the form
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'next-button-field',
  templateUrl: './button-field.component.html',
  imports: [I18nModule],
})
export class ButtonFieldComponent
  extends AbstractDeclarativeFieldComponent<FormActionField>
  implements OnInit
{
  private readonly actionService = inject(ActionService);
  private readonly toastService = inject(ToastMessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _log = logger({
    name: 'ButtonFieldComponent',
    category: 'declarative-forms.field',
  });

  @HostBinding('class') componentClass = 'form-field form-field__button';

  @Input({ required: true }) control!: AbstractControl;

  /**
   * Event that emits when the button was clicked
   */
  @Output() clicked = new EventEmitter();

  public isDisabled = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private actionRunning$ = new BehaviorSubject<boolean>(false);

  get value(): null {
    // Not sure if we need / want to use this.
    return null;
  }

  ngOnInit(): void {
    const { action, actionConfig } = this.field.widgetOptions;
    const { isDisabled$, action$, responseHandler } = this.initAction(
      action,
      actionConfig,
    );

    // Subscribe for changes of the disabled status
    combineLatest([
      this.actionService.isExecuteable(action).pipe(booleanInvert()),
      this.actionRunning$,
      isDisabled$,
    ])
      .pipe(
        arrayMapToSome((value) => value === true),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isDisabled) => (this.isDisabled = isDisabled));

    // Subscribe to dispatch the action
    this.clicked
      .asObservable()
      .pipe(
        switchMap(() => action$),
        switchMap((args) =>
          this.actionService.dispatchAction(action, args).pipe(
            tap((event) => {
              // Emit change of running state, when we receive an action start event
              if (isActionStartEvent(event)) {
                this.actionRunning$.next(true);
              }
            }),
            catchError((error) => {
              this.logActionError(error);
              return of({});
            }),
          ),
        ),
        filter(isActionResponse),
        tap(() => this.actionRunning$.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(responseHandler);
  }

  public onButtonClick(): void {
    this.clicked.emit();
  }

  get showError(): boolean {
    return (
      this.form && (this.value || this.control.touched) && this.control.invalid
    );
  }

  /**
   * Initializes the observables for the given `action`.
   *
   * @param action Action identifier
   * @param config Configuration for the action
   */
  private initAction(action: string, config: any): InitActionResult {
    switch (action) {
      case PhoneAction.MobileNumberValidation:
        return this.initMobilePhoneValidationAction(config);
      default:
        return createEmptyAction();
    }
  }

  /**
   * Initializes behavioural observables for action
   * `'mobile_number_validation'`.
   *
   * @param config The action configuration
   */
  private initMobilePhoneValidationAction(config: {
    phone_field_key: string;
  }): InitActionResult {
    const { phone_field_key: fieldKey } = config;

    // Check for valid config
    if (!fieldKey) {
      this._log.warn(
        `Could not initialze action ${this.field.widgetOptions.action}: No field key`,
      );
      return createEmptyAction();
    }
    // Check if the related field exists
    const relatedField = this.control.parent?.get(fieldKey);
    if (!relatedField) {
      this._log.warn(
        `Could not initialze action ${this.field.widgetOptions.action}: Related field with key ${fieldKey} not found`,
      );
      return createEmptyAction();
    }

    // Set up observable when the button should be disabled
    const isDisabled$ = combineLatest([
      relatedField.valueChanges.pipe(map((v) => !v)),
      relatedField.statusChanges.pipe(map((s) => s === 'INVALID')),
    ]).pipe(
      arrayMapToSome((shouldBeDisabled) => shouldBeDisabled === true),
      startWith(relatedField.invalid || !relatedField.value),
    );

    // Set up observable for the action to start
    const action$ = defer(() =>
      of<ValidateMobileNumberArgs>({
        phoneNumber: relatedField.value,
        language: this.form.flatValue[
          DeclarativeFormFieldKey.CorrespondenceLanguage
        ] as any,
      }),
    );

    // Set up the response handler
    const responseHandler: InitActionResult['responseHandler'] = (res) =>
      this.handleMobilePhoneValidationActionResponse(res);

    return {
      isDisabled$,
      action$,
      responseHandler,
    };
  }

  /**
   * Handles the response of the action `mobile_number_validation`.
   */
  private handleMobilePhoneValidationActionResponse(
    res: ActionResponse<SendSmsStatus>,
  ) {
    const { result } = res;
    const toast = toastMessages.mobile_number_validation[result];

    if (toast) {
      this.toastService.show({
        // Use these as defaults
        duration: 10000,
        button: { action: 'dismiss', label: 'dismiss', close: true },
        // Extend with toast for result
        ...toast,
        shown: true,
      });
    }
  }

  private logActionError(error: ActionResponseError) {
    this._log.error(
      `ButtonField: Error while executing action ${this.field.widgetOptions.action}\nMessage: ${error.message}`,
      error,
    );
  }
}
